// =====================================================================
// Duplicate display-name guard
// =====================================================================
// Two documents on the same loan may not carry the same
// `file_display_name`. Duplicates are not just untidy — they make the
// viewer's grouping, the table's search, and every "which file is this?"
// affordance ambiguous, and from an operator's seat a rename that lands on
// a name already in use is indistinguishable from a rename that silently
// reverted. Box enforces the same rule per folder (a colliding upload or
// rename comes back as a 409 `item_name_in_use`), so blocking the collision
// here keeps the LOS and the Box mirror in step instead of letting the LOS
// accept a name Box will later reject.
//
// Used by BOTH rename surfaces — the Documents tab's inline filename editor
// and the Edit Details form (UpdateLoanDocumentMetadataForm) — so the rule
// can't drift between them.

// Names are compared the way a person reads them: case-insensitively, with
// surrounding whitespace ignored.
export const normalizeDisplayName = (name) =>
  String(name ?? "")
    .trim()
    .toLowerCase();

/**
 * The OTHER document on this loan already using `name`, or null when the
 * name is free. `excludeId` is the doc being renamed — a document never
 * collides with itself.
 */
export const findDuplicateDisplayName = (docs, excludeId, name) => {
  const target = normalizeDisplayName(name);
  if (!target) return null;
  return (
    (docs || []).find(
      (d) =>
        d &&
        d._id !== excludeId &&
        normalizeDisplayName(d.file_display_name) === target,
    ) || null
  );
};

export const isDuplicateDisplayName = (docs, excludeId, name) =>
  !!findDuplicateDisplayName(docs, excludeId, name);

export const DUPLICATE_NAME_MESSAGE =
  "Another file on this loan already uses this name.";
